import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Clock,
  ShieldCheck,
  Globe,
  BarChart2,
  Calendar,
  Plus,
  MoreHorizontal,
  ExternalLink,
  Check,
  FileText,
  Shield,
  Award,
  Lock,
  Globe2,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const tabs = ['Overview', 'Timestamp Records', 'Verification', 'Time Sources', 'Authorities', 'Settings'];

const LEGEND_ITEMS = [
  { name: "GlobalSign", color: "#6366f1" },
  { name: "Sectigo", color: "#3b82f6" },
  { name: "DigiCert", color: "#22c55e" },
  { name: "NIST", color: "#f59e0b" },
];

function ResponseTimeLegend() {
  return (
    <div className="flex gap-3 text-[10px] pb-1 justify-end">
      {LEGEND_ITEMS.map(({ name, color }) => (
        <span key={name} className="flex items-center gap-1">
          <span style={{ background: color }} className="inline-block h-2 w-2 rounded-full" />
          {name}
        </span>
      ))}
    </div>
  );
}

export default function TimeSources() {
  const navigate = useNavigate();
  const [selectedSource, setSelectedSource] = useState("GlobalSign TSA");

  const timeSourcesData = [
    {
      name: "GlobalSign TSA",
      type: "Public TSA",
      authority: "GlobalSign",
      status: "Active",
      responseTime: "0.38 sec",
      lastSync: "May 31, 2025 10:30 AM",
      url: "https://timestamp.globalsign.com",
      apiEndpoint: "/tsa",
      authentication: "Mutual TLS",
      supportedAlgorithms: "SHA-256, SHA-384, SHA-512",
      lastSyncFull: "May 31, 2025 10:30:12 AM (UTC)",
      iconBg: "bg-blue-600",
    },
    {
      name: "Sectigo TSA",
      type: "Public TSA",
      authority: "Sectigo",
      status: "Active",
      responseTime: "0.41 sec",
      lastSync: "May 31, 2025 10:29 AM",
      url: "https://timestamp.sectigo.com",
      apiEndpoint: "/tsa",
      authentication: "Mutual TLS",
      supportedAlgorithms: "SHA-256, SHA-384, SHA-512",
      lastSyncFull: "May 31, 2025 10:29:40 AM (UTC)",
      iconBg: "bg-blue-600",
    },
    {
      name: "DigiCert TSA",
      type: "Public TSA",
      authority: "DigiCert",
      status: "Active",
      responseTime: "0.40 sec",
      lastSync: "May 31, 2025 10:30 AM",
      url: "https://timestamp.digicert.com",
      apiEndpoint: "/tsa",
      authentication: "Mutual TLS",
      supportedAlgorithms: "SHA-256, SHA-384, SHA-512",
      lastSyncFull: "May 31, 2025 10:30:01 AM (UTC)",
      iconBg: "bg-blue-500",
    },
    {
      name: "I-PQSign TSA (Internal)",
      type: "Internal TSA",
      authority: "Intrust",
      status: "Active",
      responseTime: "0.32 sec",
      lastSync: "May 31, 2025 10:30 AM",
      url: "https://tsa.internal.local",
      apiEndpoint: "/tsa",
      authentication: "Mutual TLS",
      supportedAlgorithms: "SHA-256, SHA-384, SHA-512",
      lastSyncFull: "May 31, 2025 10:30:00 AM (UTC)",
      iconBg: "bg-blue-600",
    },
    {
      name: "NIST Time Server",
      type: "Public NTP",
      authority: "NIST",
      status: "Active",
      responseTime: "0.45 sec",
      lastSync: "May 31, 2025 10:29 AM",
      url: "https://time.nist.gov",
      apiEndpoint: "/ntp",
      authentication: "Network Authentication",
      supportedAlgorithms: "NTPv4",
      lastSyncFull: "May 31, 2025 10:29:18 AM (UTC)",
      iconBg: "bg-blue-500",
    },
    {
      name: "Indian CERT-IN Time Source",
      type: "Government",
      authority: "CERT-IN",
      status: "Active",
      responseTime: "0.50 sec",
      lastSync: "May 31, 2025 10:28 AM",
      url: "https://cert-in.org.in",
      apiEndpoint: "/time",
      authentication: "Mutual TLS",
      supportedAlgorithms: "SHA-256, SHA-384, SHA-512",
      lastSyncFull: "May 31, 2025 10:28:42 AM (UTC)",
      iconBg: "bg-blue-600",
    },
  ];

  const selectedSourceDetails = useMemo(
    () =>
      timeSourcesData.find((source) => source.name === selectedSource) ||
      timeSourcesData[0],
    [selectedSource]
  );

  const statusCards = [
    {
      label: "Configured Time Sources",
      value: "6",
      subtitle: "All Active",
      icon: <Clock className="h-5 w-5 text-indigo-600" />,
      bg: "bg-indigo-50",
    },
    {
      label: "Trusted Sources",
      value: "6",
      subtitle: "Verified",
      icon: <ShieldCheck className="h-5 w-5 text-emerald-600" />,
      bg: "bg-emerald-50",
    },
    {
      label: "Sync Status",
      value: "100%",
      subtitle: "Healthy",
      subtitlePrefix: "+",
      icon: <Globe className="h-5 w-5 text-blue-600" />,
      bg: "bg-blue-50",
    },
    {
      label: "Avg. Response Time",
      value: "0.42 sec",
      subtitle: "12.5% vs last month",
      subtitlePrefix: "↓",
      icon: <BarChart2 className="h-5 w-5 text-blue-600" />,
      bg: "bg-blue-50",
    },
    {
      label: "Last Sync",
      value: "May 31, 2025",
      subtitle: "10:30:12 AM (UTC)",
      subtitlePrefix: "",
      icon: <Calendar className="h-5 w-5 text-indigo-600" />,
      bg: "bg-indigo-50",
      hideCheck: true,
    },
  ];

  const performanceData = [
    { date: "May 25", globalsign: 0.35, sectigo: 0.58, digicert: 0.45, nist: 0.28 },
    { date: "May 26", globalsign: 0.30, sectigo: 0.52, digicert: 0.40, nist: 0.27 },
    { date: "May 27", globalsign: 0.32, sectigo: 0.49, digicert: 0.41, nist: 0.27 },
    { date: "May 28", globalsign: 0.30, sectigo: 0.58, digicert: 0.46, nist: 0.31 },
    { date: "May 29", globalsign: 0.30, sectigo: 0.50, digicert: 0.39, nist: 0.29 },
    { date: "May 30", globalsign: 0.36, sectigo: 0.50, digicert: 0.52, nist: 0.40 },
    { date: "May 31", globalsign: 0.41, sectigo: 0.60, digicert: 0.55, nist: 0.41 },
  ];

  const healthData = [
    { name: "Healthy", value: 6, color: "#22c55e" },
    { name: "Warning", value: 0, color: "#f59e0b" },
    { name: "Offline", value: 0, color: "#ef4444" },
  ];

  const complianceStandards = [
    {
      name: "RFC 3161 (Time-Stamp Protocol)",
      icon: <FileText className="h-3.5 w-3.5 text-indigo-600" />,
      iconBg: "bg-indigo-50",
    },
    {
      name: "ETSI TS 102 023",
      icon: <Shield className="h-3.5 w-3.5 text-blue-600" />,
      iconBg: "bg-blue-50",
    },
    {
      name: "NIST SP 1800-7",
      icon: <Award className="h-3.5 w-3.5 text-blue-600" />,
      iconBg: "bg-blue-50",
    },
    {
      name: "ISO 27001:2022",
      icon: <Lock className="h-3.5 w-3.5 text-indigo-600" />,
      iconBg: "bg-indigo-50",
    },
    {
      name: "Indian CERT-IN Guidelines",
      icon: <Globe2 className="h-3.5 w-3.5 text-indigo-600" />,
      iconBg: "bg-indigo-50",
    },
  ];

  return (
    <div className="min-h-screen bg-[white] font-sans text-slate-800 -mx-6 -mt-6">
      {/* Tab Header */}
      <header className="sticky top-0 z-40 bg-white">
        <div className="mx-auto flex max-w-[1600px] items-center justify-between px-6 py-2.5">
          <nav className="flex gap-6">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => {
                  if (tab === 'Time Sources') return;
                  if (tab === 'Overview') navigate('/timestamps');
                  else if (tab === 'Timestamp Records') navigate('/timestamps/records');
                  else if (tab === 'Verification') navigate('/timestamps/verification');
                  else if (tab === 'Authorities') navigate('/timestamps/authorities');
                  else if (tab === 'Settings') navigate('/timestamps/settings');
                  else navigate('/timestamps');
                }}
                className={`pb-1.5 text-xs font-medium transition-colors ${
                  tab === 'Time Sources'
                    ? 'border-b-2 border-indigo-600 text-indigo-600'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                {tab}
              </button>
            ))}
          </nav>
          <button className="rounded-lg bg-indigo-600 px-3.5 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-700 transition">
            + Add Time Source
          </button>
        </div>
      </header>
      <div className="mx-auto max-w-[1500px] space-y-4 px-6 pt-4">
        {/* Top 5 KPI Cards */}
        <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
          {statusCards.map((card) => (
            <div
              key={card.label}
              className="flex items-center justify-between rounded-xl border border-slate-100 bg-white p-3.5 shadow-sm"
            >
              <div className="flex items-center gap-3">
                <div className={`rounded-lg p-2.5 ${card.bg}`}>
                  {card.icon}
                </div>
                <div>
                  <p className="text-[11px] font-medium text-slate-500">
                    {card.label}
                  </p>
                  <p className="text-xl font-bold text-slate-900">
                    {card.value}
                  </p>
                  <p className="mt-0.5 flex items-center gap-1 text-[11px] font-medium text-emerald-600">
                    {!card.hideCheck && (
                      <span>{card.subtitlePrefix || "✓"}</span>
                    )}
                    <span>{card.subtitle}</span>
                  </p>
                </div>
              </div>
            </div>
          ))}
        </section>

        {/* Main Section */}
        <section className="grid grid-cols-1 gap-4 lg:grid-cols-12">
          {/* Left Column (7 cols) */}
          <div className="space-y-4 lg:col-span-7">
            {/* Time Source List */}
            <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-base font-bold text-slate-900">
                  Time Source List
                </h2>
                <button className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3.5 py-1.5 text-xs font-semibold text-white transition hover:bg-indigo-700">
                  <Plus className="h-3.5 w-3.5" />
                  Add Source
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-slate-100 text-[11px] font-semibold text-slate-400">
                      <th className="pb-2.5 font-medium">Name</th>
                      <th className="pb-2.5 font-medium">Type</th>
                      <th className="pb-2.5 font-medium">Authority</th>
                      <th className="pb-2.5 font-medium">Status</th>
                      <th className="pb-2.5 font-medium">Response Time</th>
                      <th className="pb-2.5 font-medium">Last Sync</th>
                      <th className="pb-2.5 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {timeSourcesData.map((source) => (
                      <tr key={source.name} className="hover:bg-slate-50/60">
                        <td className="py-2.5 pr-2">
                          <button
                            onClick={() => setSelectedSource(source.name)}
                            className="flex items-center gap-2 font-semibold text-slate-800 hover:text-indigo-600"
                          >
                            <span
                              className={`h-2.5 w-2.5 rounded-full ${
                                selectedSource === source.name
                                  ? "bg-indigo-600"
                                  : "bg-blue-600"
                              }`}
                            />
                            <span className="truncate">{source.name}</span>
                          </button>
                        </td>
                        <td className="py-2.5 text-slate-600">{source.type}</td>
                        <td className="py-2.5 text-slate-600">
                          {source.authority}
                        </td>
                        <td className="py-2.5">
                          <span className="rounded-md bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-600">
                            {source.status}
                          </span>
                        </td>
                        <td className="py-2.5 font-medium text-slate-700">
                          {source.responseTime}
                        </td>
                        <td className="py-2.5 text-[11px] text-slate-500">
                          {source.lastSync}
                        </td>
                        <td className="py-2.5">
                          <button className="text-slate-400 hover:text-slate-600">
                            <MoreHorizontal className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Bottom Sub-grid (Performance + Compliance) */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
              {/* Performance Chart (7 cols) */}
              <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm md:col-span-7">
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-sm font-bold text-slate-900">Time Source Performance</h3>
                </div>

                <div className="h-[210px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={performanceData}
                      margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid
                        stroke="#f1f5f9"
                        vertical={false}
                        strokeDasharray="0"
                      />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 10, fill: "#64748b" }}
                        tickLine={false}
                        axisLine={{ stroke: "#f1f5f9" }}
                      />
                      <YAxis
                        width={90}
                        domain={[0.0, 1.0]}
                        ticks={[0.0, 0.2, 0.4, 0.6, 0.8, 1.0]}
                        tick={{ fontSize: 10, fill: "#64748b" }}
                        tickLine={false}
                        axisLine={false}
                        label={{
                          value: "Response Time (seconds)",
                          angle: -90,
                          position: "insideLeft",
                          offset: 20,
                          style: { fontSize: 11, fill: "#475569", textAnchor: "middle", fontWeight: 500 },
                        }}
                      />
                      <Tooltip
                        contentStyle={{
                          borderRadius: "8px",
                          border: "1px solid #f1f5f9",
                          fontSize: "11px",
                        }}
                      />
                      <Legend
                        verticalAlign="top"
                        align="left"
                        iconType="circle"
                        iconSize={6}
                        wrapperStyle={{ fontSize: "10px", top: -5 }}
                        content={<ResponseTimeLegend />}
                      />
                      <Line type="monotone" dataKey="globalsign" name="GlobalSign" stroke="#6366f1" strokeWidth={2} dot={{ r: 2.5 }} />
                      <Line type="monotone" dataKey="sectigo" name="Sectigo" stroke="#3b82f6" strokeWidth={2} dot={{ r: 2.5 }} />
                      <Line type="monotone" dataKey="digicert" name="DigiCert" stroke="#22c55e" strokeWidth={2} dot={{ r: 2.5 }} />
                      <Line type="monotone" dataKey="nist" name="NIST" stroke="#f59e0b" strokeWidth={2} dot={{ r: 2.5 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Compliance & Standards (5 cols) */}
              <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm md:col-span-5">
                <h3 className="mb-3 text-sm font-bold text-slate-900">
                  Compliance & Standards
                </h3>

                <div className="space-y-3">
                  {complianceStandards.map((item) => (
                    <div
                      key={item.name}
                      className="flex items-center justify-between text-xs"
                    >
                      <div className="flex items-center gap-2.5">
                        <div className={`rounded-md p-1.5 ${item.iconBg}`}>
                          {item.icon}
                        </div>
                        <span className="font-medium text-slate-700 text-[11px]">
                          {item.name}
                        </span>
                      </div>
                      <span className="flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-600">
                        <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-emerald-500">
                          <Check className="h-2 w-2 text-white" />
                        </span>
                        Compliant
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column (5 cols) */}
          <div className="flex flex-col gap-3 lg:col-span-5">
            {/* Time Source Health */}
            <div className="rounded-2xl border border-slate-100 bg-white p-3 shadow-sm">
              <h3 className="mb-2 text-sm font-bold text-slate-900">Time Source Health</h3>
              <div className="flex items-center gap-4">
                <div className="relative flex items-center justify-center shrink-0">
                  <PieChart width={160} height={160}>
                    <Pie data={healthData} dataKey="value" cx="50%" cy="50%" innerRadius={54} outerRadius={72} startAngle={90} endAngle={-270} stroke="none">
                      {healthData.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
                    </Pie>
                  </PieChart>
                  <div className="absolute text-center">
                    <p className="text-xl font-bold leading-tight text-slate-900">100%</p>
                    <p className="text-[10px] font-medium text-slate-500">Healthy</p>
                  </div>
                </div>
                <div className="flex flex-col gap-2 text-xs">
                  {[{color:"bg-emerald-500",label:"Healthy",val:"6 (100%)"},{color:"bg-amber-500",label:"Warning",val:"0 (0%)"},{color:"bg-red-500",label:"Offline",val:"0 (0%)"}].map(({color,label,val})=>(
                    <div key={label} className="flex items-center gap-2">
                      <span className={`h-3 w-3 rounded-full shrink-0 ${color}`} />
                      <span className="text-sm font-semibold text-slate-700">{label}</span>
                      <span className="text-sm font-bold text-slate-900">{val}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Time Source Details */}
            <div className="flex flex-col flex-1 rounded-2xl border border-slate-100 bg-white p-3 shadow-sm">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-sm font-bold text-slate-900">
                  Time Source Details
                </h3>

                <div className="relative">
                  <select
                    value={selectedSource}
                    onChange={(e) => setSelectedSource(e.target.value)}
                    className="rounded-lg border border-slate-200 bg-white pl-7 pr-2.5 py-1 text-xs font-semibold text-indigo-600 outline-none hover:border-slate-300"
                  >
                    {timeSourcesData.map((source) => (
                      <option key={source.name} value={source.name}>
                        {source.name}
                      </option>
                    ))}
                  </select>
                  <Globe className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-blue-600 pointer-events-none" />
                </div>
              </div>

              <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="relative flex h-7 w-7 items-center justify-center rounded-full border-2 border-blue-600 bg-blue-600">
                    <span className="h-3 w-3 rounded-full bg-black" />
                    <span className="absolute top-0.5 left-0.5 h-1.5 w-1.5 rounded-full bg-white" />
                  </div>
                  <span className="font-bold text-slate-900">
                    {selectedSourceDetails.name}
                  </span>
                </div>
                <span className="rounded-md bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-600">
                  Active
                </span>
              </div>

              <div className="flex-1 space-y-1 text-sm">
                {[
                  { label: "Type", value: selectedSourceDetails.type },
                  { label: "Authority", value: selectedSourceDetails.authority },
                  { label: "URL", value: selectedSourceDetails.url, isLink: true },
                  { label: "API Endpoint", value: selectedSourceDetails.apiEndpoint, isMono: true },
                  { label: "Authentication", value: selectedSourceDetails.authentication },
                  { label: "Supported Algorithms", value: selectedSourceDetails.supportedAlgorithms },
                  { label: "Response Time", value: `${selectedSourceDetails.responseTime} (avg)` },
                  { label: "Last Sync", value: selectedSourceDetails.lastSyncFull },
                ].map(({ label, value, isLink, isMono }) => (
                  <div key={label} className="flex gap-2 border-b border-slate-50 pb-1">
                    <span className="w-36 shrink-0 text-slate-500 text-xs">{label}</span>
                    {isLink ? (
                      <a href={value} target="_blank" rel="noreferrer" className="font-medium text-blue-600 hover:underline break-all">{value}</a>
                    ) : (
                      <span className={`${isMono ? "font-mono" : "font-medium"} text-slate-800`}>{value}</span>
                    )}
                  </div>
                ))}
              </div>

              <button className="mt-2 self-start inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1 text-xs font-semibold text-indigo-600 transition hover:bg-slate-50">
                View Certificate
                <ExternalLink className="h-3 w-3" />
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}